/**
 * Detect cooking durations inside free-form recipe step text so we can
 * surface "tap to start a timer" chips inline.
 *
 * Strategy:
 *   - Scan for `<number>(–<number>)? <unit>` patterns.
 *   - Also support "1 hr 30 min" composites in a single match.
 *   - For ranges (e.g. "8–10 minutes") we resolve to the UPPER bound — under-
 *     cooking is worse than a chime that rings slightly late.
 *   - Return character offsets so the renderer can interleave React chips
 *     into the surrounding text without losing punctuation/spacing.
 */

export type DurationMatch = {
  start: number;
  end: number;
  /** Raw substring as it appears in the source text. */
  text: string;
  /** Total seconds for the timer. */
  seconds: number;
  /** Human label, e.g. "10 mins" or "1 hr 30 min" (mirrors `text`). */
  label: string;
};

const UNIT_SECONDS: Record<string, number> = {
  s: 1,
  sec: 1,
  secs: 1,
  second: 1,
  seconds: 1,
  m: 60,
  min: 60,
  mins: 60,
  minute: 60,
  minutes: 60,
  h: 3600,
  hr: 3600,
  hrs: 3600,
  hour: 3600,
  hours: 3600,
};

// One number, optional range upper bound, then a unit. The single-letter `m`
// and `s` units would match too aggressively in normal prose, so they're only
// allowed when followed by punctuation / end-of-string. We require a word
// boundary on the leading number to avoid matching "v2" etc.
//
// The (?<![A-Za-z]) lookbehind on the number prevents matching trailing chars
// of words like "step10". Most browsers we target support lookbehind.
const DURATION_RE =
  /(?<![A-Za-z\d])(\d+(?:\.\d+)?)(?:\s*[-–—]\s*(\d+(?:\.\d+)?))?\s*(seconds?|secs?|minutes?|mins?|hours?|hrs?|h|m|s)\b/gi;

const isLongUnit = (u: string) => u.length > 1;

const unitToSeconds = (qty: number, unit: string): number => {
  const key = unit.toLowerCase();
  const mult = UNIT_SECONDS[key] ?? 0;
  return Math.round(qty * mult);
};

export function parseDurations(text: string): DurationMatch[] {
  if (!text) return [];
  const matches: DurationMatch[] = [];
  let m: RegExpExecArray | null;
  DURATION_RE.lastIndex = 0;
  while ((m = DURATION_RE.exec(text)) !== null) {
    const [raw, lowStr, highStr, unit] = m;
    const low = parseFloat(lowStr);
    const high = highStr ? parseFloat(highStr) : low;
    const qty = Math.max(low, high); // upper bound for ranges

    // Single-letter units only count when followed by whitespace / punctuation
    // / end. Otherwise we'd grab the "m" out of "make".
    if (!isLongUnit(unit)) {
      const after = text[m.index + raw.length];
      if (after && /[A-Za-z]/.test(after)) continue;
    }

    const seconds = unitToSeconds(qty, unit);
    if (seconds <= 0) continue;
    // Skip absurdly long durations — likely a misparse, and not useful for a
    // hands-free kitchen timer (>12h).
    if (seconds > 12 * 3600) continue;
    // Also skip very short timers (< 5s) — usually a misparse like "2 s" of
    // some abbreviation.
    if (seconds < 5) continue;

    matches.push({
      start: m.index,
      end: m.index + raw.length,
      text: raw,
      seconds,
      label: raw.trim(),
    });
  }
  // Coalesce adjacent matches separated only by whitespace + "and"/"+" — e.g.
  // "1 hr 30 min" arrives as two matches; merge into one timer summing both.
  return mergeComposites(text, matches);
}

function mergeComposites(text: string, matches: DurationMatch[]): DurationMatch[] {
  if (matches.length < 2) return matches;
  const merged: DurationMatch[] = [];
  let i = 0;
  while (i < matches.length) {
    let cur = matches[i];
    while (i + 1 < matches.length) {
      const next = matches[i + 1];
      const gap = text.slice(cur.end, next.start);
      // Allow "1 hr 30 min", "1 hour and 30 minutes", "1h 30m"
      if (gap.length <= 6 && /^[\s,]*(?:and\s+)?$/i.test(gap)) {
        cur = {
          start: cur.start,
          end: next.end,
          text: text.slice(cur.start, next.end),
          seconds: cur.seconds + next.seconds,
          label: text.slice(cur.start, next.end).trim(),
        };
        i++;
      } else {
        break;
      }
    }
    merged.push(cur);
    i++;
  }
  return merged;
}

/**
 * Curated cooking verbs. Stored as base forms; we match against any of
 * the common inflections (verb, verb+s, verb+ed, verb+ing) so "simmering"
 * still resolves to "Simmer".
 *
 * Order matters loosely: multi-word entries (e.g. "stir-fry", "stir fry")
 * are checked first so "stir" doesn't pre-empt "stir-fry".
 */
const COOKING_VERBS: string[] = [
  "stir-fry",
  "stir fry",
  "deep-fry",
  "deep fry",
  "pan-fry",
  "pan fry",
  "shallow fry",
  "slow cook",
  "pressure cook",
  "air fry",
  "bain-marie",
  "sous vide",
  "bake",
  "roast",
  "broil",
  "grill",
  "fry",
  "saute",
  "sauté",
  "sear",
  "brown",
  "boil",
  "simmer",
  "poach",
  "steam",
  "blanch",
  "reduce",
  "braise",
  "stew",
  "marinate",
  "marinade",
  "rest",
  "rise",
  "prove",
  "proof",
  "chill",
  "cool",
  "freeze",
  "refrigerate",
  "set",
  "soak",
  "infuse",
  "steep",
  "knead",
  "whisk",
  "whip",
  "beat",
  "mix",
  "stir",
  "fold",
  "toast",
  "char",
  "caramelise",
  "caramelize",
  "reduce",
  "thicken",
  "cook",
  "heat",
  "warm",
  "preheat",
  "ferment",
  "smoke",
  "cure",
  "pickle",
];

const ARTICLES = new Set(["the", "a", "an", "some", "any", "your", "my", "this", "these", "those"]);

/** Capitalise the first letter, preserving the rest as-is. */
const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/**
 * Normalise an inflected verb to its base form so the resulting label reads
 * like an imperative ("Simmer sauce", not "Simmering sauce"). Handles common
 * English patterns; falls back to the input unchanged.
 */
const normalizeVerb = (verb: string): string => {
  const lower = verb.toLowerCase();
  // Exact match against the curated base list?
  if (COOKING_VERBS.includes(lower)) return capitalize(lower);
  // Try stripping common inflections and re-checking the curated list.
  const candidates: string[] = [];
  if (lower.endsWith("ing")) {
    // baking → bake, simmering → simmer, stirring → stir
    candidates.push(lower.slice(0, -3) + "e", lower.slice(0, -3), lower.slice(0, -4));
  }
  if (lower.endsWith("ed")) {
    candidates.push(lower.slice(0, -2) + "e", lower.slice(0, -2), lower.slice(0, -3));
  }
  if (lower.endsWith("s") && !lower.endsWith("ss")) {
    candidates.push(lower.slice(0, -1));
  }
  for (const c of candidates) {
    if (COOKING_VERBS.includes(c)) return capitalize(c);
  }
  return capitalize(lower);
};

const STOP_WORDS = new Set([
  "for",
  "until",
  "about",
  "approximately",
  "approx",
  "around",
  "roughly",
  "over",
  "on",
  "in",
  "at",
  "with",
]);

const MAX_LABEL_LEN = 36;

/**
 * Derive a "Verb + Object" timer label from the surrounding step text. This
 * makes the timer dock far easier to scan than the generic "Step N – 10 mins"
 * fallback. Returns null when no confident label can be produced.
 *
 * Algorithm:
 *  1. Look at the clause leading up to the duration (split on punctuation /
 *     "for" / "until" so we don't drag in unrelated context).
 *  2. Find a known cooking verb anywhere in that clause; capture the words
 *     between the verb and the duration as the object.
 *  3. Strip leading articles ("the", "a", "your"), normalise the verb to its
 *     base form, and assemble "Verb object". Fall back to the verb alone if
 *     no object is present, and cap the total length.
 */
export function deriveTimerLabel(step: string, match: DurationMatch): string | null {
  if (!step || !match) return null;
  const before = step.slice(0, match.start);
  if (!before.trim()) return null;

  // Try progressively wider scopes: nearest soft clause first (so "Now boil
  // eggs" wins over "Heat pan"), but if that clause has no cooking verb,
  // fall back to the full preceding sentence. This handles patterns like
  // "Sear the steak until golden, about 4 minutes per side." where the verb
  // sits before a soft delimiter.
  const sentenceStart = Math.max(
    before.lastIndexOf(". "),
    before.lastIndexOf("! "),
    before.lastIndexOf("? "),
  );
  const sentence = (sentenceStart >= 0 ? before.slice(sentenceStart + 2) : before).trim();

  const softSplit = Math.max(
    sentence.lastIndexOf("; "),
    sentence.lastIndexOf(", "),
    sentence.lastIndexOf(": "),
    sentence.lastIndexOf(" – "),
    sentence.lastIndexOf(" — "),
  );
  const nearestClause = (softSplit >= 0 ? sentence.slice(softSplit + 2) : sentence).trim();

  const scopes = nearestClause === sentence ? [sentence] : [nearestClause, sentence];

  let verbEnd = -1;
  let verbBase: string | null = null;
  let chosenTokens: string[] = [];

  for (const scope of scopes) {
    const tokens = scope.split(/\s+/).filter(Boolean);
    if (tokens.length === 0) continue;
    let ve = -1;
    let vb: string | null = null;
    for (let i = 0; i < tokens.length; i++) {
      for (let span = Math.min(2, tokens.length - i); span >= 1; span--) {
        const phrase = tokens
          .slice(i, i + span)
          .join(" ")
          .toLowerCase()
          .replace(/[^a-z\- ]/g, "");
        if (!phrase) continue;
        const normalised = normalizeVerb(phrase);
        if (COOKING_VERBS.includes(normalised.toLowerCase())) {
          ve = i + span;
          vb = normalised;
          break;
        }
      }
    }
    if (vb !== null) {
      verbEnd = ve;
      verbBase = vb;
      chosenTokens = tokens;
      break;
    }
  }
  if (verbBase === null) return null;

  // Object = words between verb and end of scope, dropped at stop words.
  const objectTokens: string[] = [];
  for (let i = verbEnd; i < chosenTokens.length; i++) {
    const cleaned = chosenTokens[i].replace(/[,.;:!?()"']/g, "");
    const lower = cleaned.toLowerCase();
    if (!cleaned) continue;
    if (STOP_WORDS.has(lower)) break;
    if (objectTokens.length === 0 && ARTICLES.has(lower)) continue;
    objectTokens.push(cleaned);
    if (objectTokens.length >= 4) break;
  }

  let label = verbBase;
  if (objectTokens.length > 0) {
    label = `${verbBase} ${objectTokens.join(" ")}`;
  }
  // Trim trailing connectors that sometimes sneak in.
  label = label.replace(/\s+(and|or)$/i, "").trim();
  if (label.length > MAX_LABEL_LEN) {
    label = label.slice(0, MAX_LABEL_LEN - 1).trimEnd() + "…";
  }
  return label || null;
}

/** Format a remaining-seconds count as `M:SS` or `H:MM:SS`. */
export function formatRemaining(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(ss)}`;
  return `${m}:${pad(ss)}`;
}
